import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDispatch } from 'react-redux';

import {
  handleUserRegistration,
  RegistrationValues,
  Register,
  lottieAnimations,
  handleLoggingIn,
  BackrsUser,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { ENVIRONMENT_URL } from '@backrs_ui/grow-ui/src/settings';
import { fetchWrapper as fetch } from '@backrs_ui/grow-ui/src/utils/fetch/fetchWrapper';
import { handleAfterLogin } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

import { SxProps, Theme, Box } from '@mui/material';
import Lottie from 'lottie-react';

// no need for a thunk since we're not caring about state here
async function getRegisterInfoFromBackend(registerId: string) {
  const response = await fetch(`${ENVIRONMENT_URL}/user-leads/register_id/${registerId}`);
  const data = await response.json();

  return data;
}

export const RegistrationPage = () => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [registerInfo, setRegisterInfo] = useState<Pick<
    BackrsUser,
    'first_name' | 'last_name' | 'email'
  > | null>(null);

  // get params from url
  const params = new URLSearchParams(window.location.search);
  const registerId = params.get('register_id');

  useEffect(() => {
    if (!registerId) {
      setIsLoading(false);

      return;
    }

    const handleCallingRegisterInfo = async () => {
      setIsLoading(true);
      try {
        const test = await getRegisterInfoFromBackend(registerId);

        setRegisterInfo(test);
      } catch (error) {
        console.error('Failed to fetch registration data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    handleCallingRegisterInfo();
  }, [registerId]);

  const afterRegistration = async (registrationResult: { email: string; password: string }) => {
    try {
      // After successful registration, automatically log the user in using the same handler as LoginPageContainer
      await handleLoggingIn({
        phone: '',
        email: registrationResult.email,
        password: registrationResult.password,
        environmentUrl: ENVIRONMENT_URL,
        afterLogin: (loginResponse) =>
          handleAfterLogin({
            access_token: loginResponse.access_token,
            dispatch,
            navigate,
            setErrorMessage,
          }),
        setErrorMessage,
      });
    } catch (error) {
      console.error('Error during automatic login:', error);
      // If auto-login fails, still redirect to login page
      navigate(APP_ROUTE.LOGIN);
    }
  };

  const handleRegistration = async (payload: RegistrationValues) => {
    await handleUserRegistration({
      payload,
      afterRegistration: () =>
        afterRegistration({
          email: payload.email,
          password: payload.password,
        }),
      environmentUrl: ENVIRONMENT_URL,
      setErrorMessage,
    });
  };

  if (registerId && isLoading) {
    return (
      <Box sx={loadingSpinnerStyle}>
        <Lottie animationData={lottieAnimations.dotsDotting} loop />
      </Box>
    );
  }

  return (
    <Register
      handleRegistration={handleRegistration}
      errorMessage={errorMessage}
      registerId={registerId}
      preFilledData={registerInfo}
    />
  );
};

const loadingSpinnerStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100svh',
};
